import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, Loader, Maximize2 } from 'lucide-react';
import { config } from '../utils/config';
import { useToast } from './ui/Toast';
import { errorMessage } from '../utils/errors';

declare global {
  interface Window {
    pdfjsLib: any;
    __pdfjsLoaderPromise?: Promise<any>;
  }
}

// Interface for container element
interface PDFContainer extends HTMLDivElement {
  pdfDoc?: any;
}

interface PDFViewerProps {
  documentId: string | number;
  filename?: string;
}

const PDFJS_VERSION = '3.11.174';
const MIN_SCALE = 0.4;
const MAX_SCALE = 5;

// Force HTTPS for document URLs in production
const getSecureUrl = (path: string) => {
  let baseUrl = config.API_URL;

  if (process.env.NODE_ENV === 'production' && baseUrl.startsWith('http:')) {
    baseUrl = baseUrl.replace('http:', 'https:');
  }

  return `${baseUrl}/${path.startsWith('/') ? path.substring(1) : path}`;
};

// Load pdf.js exactly once for the whole app and reuse it across instances.
const loadPdfJsLibrary = (): Promise<any> => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (window.__pdfjsLoaderPromise) return window.__pdfjsLoaderPromise;

  window.__pdfjsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      } catch (err) {
        reject(err);
      }
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js library'));
    document.body.appendChild(script);
  });

  return window.__pdfjsLoaderPromise;
};

const PDFViewer: React.FC<PDFViewerProps> = ({ documentId, filename }) => {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.2);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<PDFContainer>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const renderTaskRef = useRef<any>(null);
  // Tracks whether the user has manually zoomed; if not we auto-fit to width.
  const userZoomedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Download the source PDF.
   *
   * This used to be a plain <a href download>. The route requires a Bearer
   * token held in localStorage, not a cookie, so the browser sent nothing and
   * every click downloaded a 401 body instead of the file. Fetch it the same
   * way loadPdf() already fetches the view URL, then save the blob.
   */
  const downloadPdf = useCallback(async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        getSecureUrl(`${config.endpoints.files}/${documentId}/download`),
        { headers: { Authorization: `Bearer ${token || ''}` } }
      );
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `document-${documentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to download the PDF.'));
    } finally {
      setDownloading(false);
    }
  }, [documentId, filename, toast]);

  const loadPdf = useCallback(async () => {
    if (!window.pdfjsLib || !isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      const pdfUrl = getSecureUrl(`${config.endpoints.files}/${documentId}/file`);
      const token = localStorage.getItem('token');

      const response = await fetch(pdfUrl, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }

      const pdfData = await response.arrayBuffer();
      if (!isMountedRef.current) return;

      const loadingTask = window.pdfjsLib.getDocument({ data: pdfData });
      const pdf = await loadingTask.promise;

      if (isMountedRef.current && containerRef.current) {
        containerRef.current.pdfDoc = pdf;
        setNumPages(pdf.numPages);
        setPageNum(1);
        userZoomedRef.current = false;
        setLoading(false);
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      }
    }
  }, [documentId]);

  // Load the PDF.js library once, then load the document.
  useEffect(() => {
    let cancelled = false;
    loadPdfJsLibrary()
      .then(() => {
        if (!cancelled && isMountedRef.current) loadPdf();
      })
      .catch((err) => {
        if (!cancelled && isMountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to load PDF.js');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadPdf]);

  const renderPage = useCallback(async () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const scrollEl = scrollRef.current;

    if (!container?.pdfDoc || !canvas || !isMountedRef.current) return;

    // Cancel any in-flight render to avoid "multiple render" canvas errors.
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        /* ignore */
      }
      renderTaskRef.current = null;
    }

    try {
      const pdfDoc = container.pdfDoc;
      const page = await pdfDoc.getPage(pageNum);
      if (!isMountedRef.current) return;

      const context = canvas.getContext('2d');
      if (!context) throw new Error('Could not get canvas context');

      // Auto-fit to the available width on first render / window resize,
      // unless the user has explicitly chosen a zoom level.
      let effectiveScale = scale;
      if (!userZoomedRef.current && scrollEl) {
        const baseViewport = page.getViewport({ scale: 1 });
        const available = scrollEl.clientWidth - 32; // minus padding
        if (available > 0) {
          effectiveScale = Math.min(
            MAX_SCALE,
            Math.max(MIN_SCALE, available / baseViewport.width)
          );
          if (Math.abs(effectiveScale - scale) > 0.001) {
            setScale(effectiveScale);
            return; // re-render triggered by scale state change
          }
        }
      }

      // Render at device pixel ratio for crisp output, then down-scale via CSS.
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: effectiveScale });

      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      const renderContext = {
        canvasContext: context,
        viewport,
        transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
      };

      const task = page.render(renderContext);
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (err: any) {
      if (err?.name === 'RenderingCancelledException') return;
      console.error('Error rendering page:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to render page');
      }
    }
  }, [pageNum, scale]);

  // Render whenever page/scale changes or the doc finishes loading.
  useEffect(() => {
    if (!loading) renderPage();
  }, [renderPage, loading]);

  // Re-fit on container resize (e.g. entities split view, window resize).
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const observer = new ResizeObserver(() => {
      if (!userZoomedRef.current && !loading) renderPage();
    });
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [renderPage, loading]);

  const nextPage = () => setPageNum((p) => Math.min(p + 1, numPages));
  const prevPage = () => setPageNum((p) => Math.max(p - 1, 1));

  const zoomIn = () => {
    userZoomedRef.current = true;
    setScale((s) => Math.min(Math.round((s + 0.2) * 100) / 100, MAX_SCALE));
  };
  const zoomOut = () => {
    userZoomedRef.current = true;
    setScale((s) => Math.max(Math.round((s - 0.2) * 100) / 100, MIN_SCALE));
  };
  const fitToWidth = () => {
    userZoomedRef.current = false;
    renderPage();
  };

  // Ctrl/Cmd + wheel to zoom.
  const handleWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    userZoomedRef.current = true;
    setScale((s) => {
      const next = e.deltaY < 0 ? s + 0.15 : s - 0.15;
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(next * 100) / 100));
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Document header */}
      <div className="flex items-center justify-between bg-coral-500/10 border-b border-coral-500/20 p-4">
        <h3 className="text-lg font-medium text-foreground truncate">
          {filename || `Document #${documentId}`}
        </h3>

        {!loading && !error && (
          <div className="flex items-center space-x-1">
            <button
              onClick={zoomOut}
              title="Zoom out"
              className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span className="text-foreground-muted w-12 text-center text-sm">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              title="Zoom in"
              className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              onClick={fitToWidth}
              title="Fit to width"
              className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded"
            >
              <Maximize2 className="w-5 h-5" />
            </button>
            <button
              onClick={downloadPdf}
              disabled={downloading}
              title="Download"
              className="p-2 text-foreground-muted hover:bg-coral-500/20 rounded ml-1 disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* PDF Viewer */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden">
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
            <Loader className="w-8 h-8 text-coral-400 animate-spin" />
            <p className="mt-2 text-foreground-muted">Loading PDF...</p>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-black/50">
            <p className="text-red-300">{error}</p>
            <button
              onClick={loadPdf}
              className="mt-2 px-4 py-2 bg-coral-500/20 hover:bg-coral-500/30 text-foreground-muted rounded"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/*
              Scroll container holds an inner wrapper that grows to the canvas
              size. min-w-full / min-h-full keeps small pages centered, while a
              larger page makes the wrapper exceed the viewport so the WHOLE
              document is reachable by scrolling in every direction.
            */}
            <div
              ref={scrollRef}
              onWheel={handleWheel}
              className="absolute inset-0 overflow-auto bg-black/50"
            >
              <div className="min-w-full min-h-full flex items-center justify-center p-4">
                <canvas ref={canvasRef} className="shadow-lg block" />
              </div>
            </div>

            {/* Page navigation – fixed over the viewport, not scrolling away */}
            {numPages > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center bg-black/70 backdrop-blur rounded-full px-4 py-2 shadow-lg">
                <button
                  onClick={prevPage}
                  disabled={pageNum <= 1}
                  className="p-1 text-foreground-muted hover:bg-coral-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="mx-2 text-foreground text-sm">
                  {pageNum} / {numPages}
                </span>
                <button
                  onClick={nextPage}
                  disabled={pageNum >= numPages}
                  className="p-1 text-foreground-muted hover:bg-coral-500/20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;
